import { useEffect, useMemo, useState } from "react";
import { Star, Quote, Camera } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

const FALLBACK_REVIEWS = [
  {
    id: "fb-1",
    name: "Lauren M.",
    town: "Pine Hill",
    rating: 5,
    quote:
      "Isaac was professional from the first knock. My bins haven't smelled like this since the day I bought them.",
    avatar_color: "#0EA5E9",
    photo_url: "/testimonials/testimonial-1.jpg",
  },
];

function useReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const handler = (e) => setReduced(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else mq.removeListener(handler);
    };
  }, []);
  return reduced;
}

function StarRow({ count = 5, animate = false }) {
  return (
    <div className="flex items-center gap-1 text-gold" data-testid="review-stars">
      {Array.from({ length: count }).map((_, i) => (
        <Star
          key={i}
          size={16}
          fill="currentColor"
          stroke="none"
          className={animate ? "animate-review-star-pop" : ""}
          style={animate ? { animationDelay: `${i * 60}ms` } : undefined}
        />
      ))}
    </div>
  );
}

function initialsOf(name) {
  return name
    .split(/[\s&]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();
}

export default function Reviews() {
  const [items, setItems] = useState(null);
  const reduced = useReducedMotion();

  useEffect(() => {
    let cancelled = false;
    api
      .get("/testimonials", { params: { limit: 60 } })
      .then(({ data }) => {
        if (!cancelled)
          setItems(data.items && data.items.length ? data.items : FALLBACK_REVIEWS);
      })
      .catch(() => {
        if (!cancelled) setItems(FALLBACK_REVIEWS);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const list = items || FALLBACK_REVIEWS;
  const total = list.length;
  const avg = useMemo(
    () =>
      total
        ? (list.reduce((acc, r) => acc + (r.rating || 5), 0) / total).toFixed(1)
        : "5.0",
    [list, total],
  );

  return (
    <div data-testid="page-reviews" className="bg-white">
      <section className="relative bg-gradient-to-b from-brand-50 to-white">
        <div className="container-page relative pt-16 pb-20 md:pt-24 md:pb-24">
          <div className="max-w-3xl">
            <p className="overline mb-4">Reviews</p>
            <h1
              className="font-heading h1-fluid font-extrabold text-ink text-balance"
              data-reveal
            >
              What our <span className="text-brand">neighbors</span> say.
            </h1>
            <div
              className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-3 text-slate-600"
              data-reveal
              style={{ "--reveal-delay": "120ms" }}
            >
              <div className="flex items-center gap-3">
                <StarRow />
                <span className="font-heading text-3xl font-extrabold text-ink leading-none">
                  {avg}
                </span>
              </div>
              <span className="hidden sm:inline-block text-slate-300">·</span>
              <p className="text-sm text-slate-600 leading-snug max-w-md">
                Based on <span className="font-bold text-ink">{total}</span>{" "}
                verified reviews from{" "}
                <span className="text-ink font-semibold">
                  Pine Hill and nearby South Jersey communities
                </span>
                .
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="section-padding bg-white" data-testid="reviews-section">
        <div className="container-page">
          {items === null ? (
            <div
              className="flex items-center justify-center min-h-[260px]"
              data-testid="reviews-loading"
              aria-live="polite"
            >
              <div className="h-7 w-7 rounded-full border-2 border-brand-200 border-t-brand animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {list.map((r, i) => (
                <ReviewCard key={r.id || r.name} r={r} index={i} reduced={reduced} />
              ))}
            </div>
          )}

          <div className="mt-20 text-center" data-reveal>
            <p className="overline mb-3">Already a customer?</p>
            <h3 className="font-heading text-3xl sm:text-4xl font-extrabold text-ink mb-6">
              Tell your neighbors about us.
            </h3>
            <button
              type="button"
              className="btn-primary"
              onClick={(e) => e.preventDefault()}
              data-testid="leave-review-cta"
            >
              Leave a Review
            </button>
            <p className="mt-3 text-xs text-slate-400">
              Review submission opens in the next phase.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-brand-50/40 py-16">
        <div
          className="container-page flex flex-col md:flex-row items-start md:items-center justify-between gap-6"
          data-reveal
        >
          <h3 className="font-heading text-2xl sm:text-3xl font-bold text-ink">
            Ready to join the neighborhood?
          </h3>
          <Link to="/plans" className="btn-secondary" data-testid="reviews-plans-cta">
            See Plans
          </Link>
        </div>
      </section>

      <style>{`
        @keyframes review-star-pop {
          0%   { transform: scale(0.7); opacity: 0.5; }
          60%  { transform: scale(1.12); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        .animate-review-star-pop {
          animation: review-star-pop 420ms cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        @media (prefers-reduced-motion: reduce) {
          .animate-review-star-pop { animation: none !important; }
        }
      `}</style>
    </div>
  );
}

function ReviewCard({ r, index, reduced }) {
  const [revealed, setRevealed] = useState(false);

  // Toggle the star count-up animation once the card scrolls into view (matches
  // the [data-reveal] system used elsewhere on the site).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const el = document.querySelector(`[data-review-id="${r.id || r.name}"]`);
    if (!el) return;
    if (reduced) {
      setRevealed(true);
      return;
    }
    if (typeof IntersectionObserver === "undefined") {
      setRevealed(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setRevealed(true);
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.25, rootMargin: "0px 0px -10% 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [r.id, r.name, reduced]);

  return (
    <figure
      data-reveal
      data-testid={`review-card-${index}`}
      data-review-id={r.id || r.name}
      data-photo-pending={r.photo_pending ? "true" : "false"}
      style={{ "--reveal-delay": `${(index % 3) * 80}ms` }}
      className="group relative bg-white rounded-2xl border border-slate-100 shadow-md transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-2xl overflow-hidden flex flex-col"
    >
      {/* Photo block */}
      <div
        className="relative w-full aspect-[3/2] overflow-hidden bg-slate-100"
        data-testid={`review-photo-wrap-${index}`}
      >
        {r.photo_url ? (
          <img
            src={r.photo_url}
            alt={
              r.photo_pending
                ? "Customer photo coming soon"
                : `${r.name} from ${r.town} with Silky Smooth Trash Bin Service`
            }
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.04]"
            style={{ objectPosition: r.object_position || "center" }}
            draggable={false}
            data-testid={`review-photo-img-${index}`}
            onError={(e) => {
              e.currentTarget.style.visibility = "hidden";
            }}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-slate-400">
            <Camera size={28} />
          </div>
        )}
        {r.photo_pending && (
          <span
            className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 bg-white/90 backdrop-blur text-slate-700 text-[10px] font-bold uppercase tracking-[0.18em] px-2.5 py-1 rounded-full shadow-soft"
            data-testid={`review-photo-pending-${index}`}
          >
            <Camera size={10} /> Photo coming soon
          </span>
        )}
        {/* Bottom-edge gradient so photo flows into the body section */}
        <div
          aria-hidden="true"
          className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-white to-transparent"
        />
      </div>

      {/* Body */}
      <div className="relative flex-1 flex flex-col p-6 sm:p-7">
        <Quote
          size={36}
          aria-hidden="true"
          className="absolute top-4 right-5 text-gold/35"
        />
        <StarRow count={r.rating || 5} animate={revealed && !reduced} />
        <blockquote className="mt-3 font-heading text-[17px] sm:text-lg font-medium text-ink leading-relaxed text-balance">
          &ldquo;{r.quote}&rdquo;
        </blockquote>
        <figcaption className="mt-6 flex items-center gap-3">
          <span
            aria-hidden="true"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full text-white font-heading text-sm font-bold shadow-soft"
            style={{ backgroundColor: r.avatar_color || "#0EA5E9" }}
          >
            {initialsOf(r.name)}
          </span>
          <div>
            <p className="text-sm font-bold text-ink leading-none">{r.name}</p>
            <p className="mt-1 text-[11px] uppercase tracking-[0.18em] text-slate-500">
              {r.town}
            </p>
          </div>
        </figcaption>
      </div>
    </figure>
  );
}
